import type { MenuButtonPlacement } from "./menuButton.types";

/**
 * Extracts the base placement direction from a given `MenuButtonPlacement` string.
 * This function returns the base direction: "bottom", "left", "right" or "top".
 *
 * @default "bottom".
 */
export const getBasePlacement = (placement?: MenuButtonPlacement | null) => {
  if (!placement) {
    return "bottom";
  }

  const basePlacement = placement.split("-");

  if (!basePlacement || basePlacement.length === 0) {
    return "bottom";
  }

  return basePlacement[0];
};

/**
 * Calculates the offset for a popper element based on arrow visibility and placement.
 *
 * @remarks 10px is the size displayed by the arrow (from tip to base).
 */
export const getPopperOffset = ({
  disableArrow,
  placement,
}: {
  disableArrow: boolean;
  placement?: MenuButtonPlacement | null;
}) => {
  if (disableArrow || !placement) {
    return "0, 0";
  }

  const basePlacement = getBasePlacement(placement);

  if (basePlacement === "top") {
    return "0, -10px";
  }

  if (basePlacement === "bottom") {
    return "0, 10px";
  }

  if (basePlacement === "left") {
    return "-10px, 0px";
  }

  if (basePlacement === "right") {
    return "10px, 10px";
  }

  return "0, 0";
};

/**
 * Returns the CSS `transform-origin` value based on the menu button placement.
 * @default `"center bottom"`.
 */
export const getTransformOrigin = ({
  placement,
}: {
  placement?: MenuButtonPlacement | null;
}) => {
  if (!placement) {
    return "center bottom";
  }

  const basePlacement = getBasePlacement(placement);

  if (basePlacement === "top") {
    return "center bottom";
  }

  if (basePlacement === "bottom") {
    return "center top";
  }

  if (basePlacement === "left") {
    return "right center";
  }

  if (basePlacement === "right") {
    return "left center";
  }

  return "center bottom";
};
